import {
  Component,
  ElementRef,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import {
  AbstractControl,
  Form,
  FormArray,
  FormControl,
  FormsModule,
  NonNullableFormBuilder,
  ValidationErrors,
  ValidatorFn,
} from '@angular/forms';
import {
  sharedFormValidator,
  VEHICLE_MAKE_OPTIONS_VALUES,
  VEHICLE_TYPE_OPTIONS_VALUES,
  vehicleTypeFormRules,
} from '@credit-acceptance/shareable-utilities';
import { adobeAnalytics } from 'adobe-analytics';
import { takeUntil } from 'rxjs';

import { PrequalService } from '../services/prequal.service';
import { RuntimeConfigService } from '../services/runtime-config';
import { Unsubscribe } from '../shared/classes/unsubscribe.class';

@Component({
  selector: 'capq-vehicle-type',
  templateUrl: './vehicle-type.component.html',
  styleUrls: ['./vehicle-type.component.scss'],
  standalone: false,
})
export class VehicleTypeComponent
  extends Unsubscribe
  implements OnInit, OnDestroy
{
  vehicleTypeOptions: string[] = [...VEHICLE_TYPE_OPTIONS_VALUES];
  vehicleMakeOptions: string[] = [...VEHICLE_MAKE_OPTIONS_VALUES];
  showVehicleTypeDropdown = false;
  showVehicleMakeDropdown = false;
  // Indexes used for keyboard navigation within the dropdowns
  focusedVehicleTypeIndex = 0;
  focusedVehicleMakeIndex = 0;
  maxVehicleTypes = 5;
  maxVehicleMakes = 5;
  vehicleTypeForm = this.formBuilder.group({
    vehicleType: this.formBuilder.array(
      this.vehicleTypeOptions.map(() => this.formBuilder.control(false)),
      { validators: this.vehicleTypeValidator }
    ),
    vehicleMake: this.formBuilder.array(
      this.vehicleMakeOptions.map(() => this.formBuilder.control(false)),
      { validators: this.vehicleMakeValidator }
    ),
  });
  @Input() adobeScriptLoaded = false;
  adobeLoadingTime = 0;
  isMobile = false;
  private readonly onResize = () => {
    this.isMobile = window.innerWidth <= 768;
  };
  constructor(
    private readonly formBuilder: NonNullableFormBuilder,
    public runtimeConfigService: RuntimeConfigService,
    private prequalService: PrequalService,
    private eRef: ElementRef
  ) {
    super();

    // Initialize Adobe Analytics
    //UTM code
    adobeAnalytics.init((val: boolean, lt: number) => {
      this.adobeLoadingTime = lt;
      this.adobeScriptLoaded = val;
    });
  }
  // Template refs for dropdown content containers
  @ViewChild('vehicleTypeDropdown')
  vehicleTypeDropdownRef!: ElementRef<HTMLDivElement>;
  @ViewChild('vehicleMakeDropdown')
  vehicleMakeDropdownRef!: ElementRef<HTMLDivElement>;
  get vehicleTypesFormArray(): FormArray<FormControl<boolean>> {
    const control = this.vehicleTypeForm.get('vehicleType');
    if (control instanceof FormArray) {
      return control as FormArray<FormControl<boolean>>;
    }
    throw new Error('vehicleType is not a FormArray');
  }

  get vehicleMakesFormArray(): FormArray<FormControl<boolean>> {
    const control = this.vehicleTypeForm.get('vehicleMake');
    if (control instanceof FormArray) {
      return control as FormArray<FormControl<boolean>>;
    }
    throw new Error('vehicleMake is not a FormArray');
  }

  ngOnInit(): void {
    this.isMobile = window.innerWidth <= 768;
    window.addEventListener('resize', this.onResize);

    this.vehicleTypeForm
      .get('vehicleType')
      ?.valueChanges.pipe(takeUntil(this.unsubscribe$))
      .subscribe(() => {
        this.updateVehicleTypeCheckboxStates();
      });

    this.vehicleTypeForm
      .get('vehicleMake')
      ?.valueChanges.pipe(takeUntil(this.unsubscribe$))
      .subscribe(() => {
        this.updateVehicleMakeCheckboxStates();
      });
  }

  override ngOnDestroy(): void {
    window.removeEventListener('resize', this.onResize);
    super.ngOnDestroy();
  }

  get selectedVehicleTypesLabel(): string {
    const selected = this.vehicleTypesFormArray.controls
      .map((ctrl, i) => (ctrl.value ? this.vehicleTypeOptions[i] : null))
      .filter((v) => v);
    return selected.length ? selected.join(', ') : 'Select';
  }
  get selectedVehicleMakesLabel(): string {
    const selected = this.vehicleMakesFormArray.controls
      .map((ctrl, i) => (ctrl.value ? this.vehicleMakeOptions[i] : null))
      .filter((v) => v);
    return selected.length ? selected.join(', ') : 'Select';
  }
  updateVehicleTypeCheckboxStates() {
    const controls = this.vehicleTypesFormArray.controls;
    const isAnySelected = controls[0].value; // "ANY" is at index 0

    if (isAnySelected) {
      controls.forEach((ctrl, i) => {
        if (i !== 0 && ctrl.value) {
          ctrl.setValue(false, { emitEvent: false });
        }
        if (i !== 0) {
          ctrl.disable({ emitEvent: false });
        }
      });
    } else {
      // Count selected (excluding "ANY")
      const selectedCount = controls
        .slice(1)
        .filter((ctrl) => ctrl.value).length;
      controls.forEach((ctrl, i) => {
        if (i !== 0) {
          if (!ctrl.value && selectedCount >= this.maxVehicleTypes) {
            ctrl.disable({ emitEvent: false });
          } else {
            ctrl.enable({ emitEvent: false });
          }
        }
      });
      // If any other is selected, deselect "ANY"
      if (controls.slice(1).some((ctrl) => ctrl.value)) {
        if (controls[0].value) {
          controls[0].setValue(false, { emitEvent: false });
        }
      }
    }

    const selectedTypes = controls
      .map((ctrl, i) => (ctrl.value ? this.vehicleTypeOptions[i] : null))
      .filter((v): v is string => v !== null);
    this.prequalService.setVehicleType({
      vehicleType: selectedTypes,
    });
  }

  updateVehicleMakeCheckboxStates() {
    const controls = this.vehicleMakesFormArray.controls;
    const isAnySelected = controls[0].value; // "ANY" is at index 0

    if (isAnySelected) {
      controls.forEach((ctrl, i) => {
        if (i !== 0 && ctrl.value) {
          ctrl.setValue(false, { emitEvent: false });
        }
        if (i !== 0) {
          ctrl.disable({ emitEvent: false });
        }
      });
    } else {
      // Count selected (excluding "ANY")
      const selectedCount = controls
        .slice(1)
        .filter((ctrl) => ctrl.value).length;
      controls.forEach((ctrl, i) => {
        if (i !== 0) {
          if (!ctrl.value && selectedCount >= this.maxVehicleMakes) {
            ctrl.disable({ emitEvent: false });
          } else {
            ctrl.enable({ emitEvent: false });
          }
        }
      });
      // If any other is selected, deselect "ANY"
      if (controls.slice(1).some((ctrl) => ctrl.value)) {
        if (controls[0].value) {
          controls[0].setValue(false, { emitEvent: false });
        }
      }
    }

    const selectedMakes = controls
      .map((ctrl, i) => (ctrl.value ? this.vehicleMakeOptions[i] : null))
      .filter((v): v is string => v !== null);
    this.prequalService.setVehicleMake({
      vehicleMake: selectedMakes,
    });
  }
  @HostListener('document:click', ['$event'])
  onClickOutside(event: Event) {
    const target = event.target as HTMLElement;

    // Check if click is inside any dropdown container
    const clickedInVehicleTypeDropdown = target.closest(
      '.cac-vehicle-type-section'
    );
    const clickedInVehicleMakeDropdown = target.closest(
      '.cac-vehicle-type-make-section'
    );

    // Close vehicle type dropdown if clicked outside
    if (!clickedInVehicleTypeDropdown && this.showVehicleTypeDropdown) {
      this.showVehicleTypeDropdown = false;
    }

    // Close vehicle make dropdown if clicked outside
    if (!clickedInVehicleMakeDropdown && this.showVehicleMakeDropdown) {
      this.showVehicleMakeDropdown = false;
    }
  }
  /**
   * Handles keyboard navigation for Vehicle Type dropdown.
   * Prevents page scroll with Arrow keys when dropdown is open and moves focus between options.
   */
  onVehicleTypeKeydown(event: KeyboardEvent) {
    if (!this.showVehicleTypeDropdown) {
      return;
    }
    const inputs = this.vehicleTypeDropdownRef?.nativeElement.querySelectorAll(
      'input[type="checkbox"]'
    );
    if (!inputs || inputs.length === 0) {
      return;
    }
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.focusedVehicleTypeIndex = Math.min(
          this.focusedVehicleTypeIndex + 1,
          inputs.length - 1
        );
        this.scrollIntoViewIfNeeded(
          inputs[this.focusedVehicleTypeIndex] as HTMLElement
        );
        (inputs[this.focusedVehicleTypeIndex] as HTMLInputElement).focus();
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.focusedVehicleTypeIndex = Math.max(
          this.focusedVehicleTypeIndex - 1,
          0
        );
        this.scrollIntoViewIfNeeded(
          inputs[this.focusedVehicleTypeIndex] as HTMLElement
        );
        (inputs[this.focusedVehicleTypeIndex] as HTMLInputElement).focus();
        break;
      case 'Home':
        event.preventDefault();
        this.focusedVehicleTypeIndex = 0;
        this.scrollIntoViewIfNeeded(inputs[0] as HTMLElement);
        (inputs[0] as HTMLInputElement).focus();
        break;
      case 'End':
        event.preventDefault();
        this.focusedVehicleTypeIndex = inputs.length - 1;
        this.scrollIntoViewIfNeeded(inputs[inputs.length - 1] as HTMLElement);
        (inputs[inputs.length - 1] as HTMLInputElement).focus();
        break;
      case 'Escape':
        this.showVehicleTypeDropdown = false;
        break;
      case ' ':
      case 'Enter':
        event.preventDefault();
        (inputs[this.focusedVehicleTypeIndex] as HTMLInputElement).click();
        break;
      default:
        break;
    }
  }
  /**
   * Handles keyboard navigation for Vehicle Make dropdown.
   */
  onVehicleMakeKeydown(event: KeyboardEvent) {
    if (!this.showVehicleMakeDropdown) {
      return;
    }
    const inputs = this.vehicleMakeDropdownRef?.nativeElement.querySelectorAll(
      'input[type="checkbox"]'
    );
    if (!inputs || inputs.length === 0) {
      return;
    }
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.focusedVehicleMakeIndex = Math.min(
          this.focusedVehicleMakeIndex + 1,
          inputs.length - 1
        );
        this.scrollIntoViewIfNeeded(
          inputs[this.focusedVehicleMakeIndex] as HTMLElement
        );
        (inputs[this.focusedVehicleMakeIndex] as HTMLInputElement).focus();
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.focusedVehicleMakeIndex = Math.max(
          this.focusedVehicleMakeIndex - 1,
          0
        );
        this.scrollIntoViewIfNeeded(
          inputs[this.focusedVehicleMakeIndex] as HTMLElement
        );
        (inputs[this.focusedVehicleMakeIndex] as HTMLInputElement).focus();
        break;
      case 'Home':
        event.preventDefault();
        this.focusedVehicleMakeIndex = 0;
        this.scrollIntoViewIfNeeded(inputs[0] as HTMLElement);
        (inputs[0] as HTMLInputElement).focus();
        break;
      case 'End':
        event.preventDefault();
        this.focusedVehicleMakeIndex = inputs.length - 1;
        this.scrollIntoViewIfNeeded(inputs[inputs.length - 1] as HTMLElement);
        (inputs[inputs.length - 1] as HTMLInputElement).focus();
        break;
      case 'Escape':
        this.showVehicleMakeDropdown = false;
        break;
      case ' ':
      case 'Enter':
        event.preventDefault();
        (inputs[this.focusedVehicleMakeIndex] as HTMLInputElement).click();
        break;
      default:
        break;
    }
  }

  /**
   * Scrolls the element into view within the dropdown if needed.
   */
  private scrollIntoViewIfNeeded(element: HTMLElement): void {
    element?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }
  vehicleTypeValidator(): ValidatorFn {
    return (controlToValidate: AbstractControl): ValidationErrors | null => {
      const controlName = Object.entries(
        controlToValidate.parent?.controls ?? {}
      ).find(([_, control]) => control === controlToValidate)?.[0];
      if (controlName) {
        const { errors } = sharedFormValidator(
          'vehicleTypeFormRules',
          controlName as keyof typeof vehicleTypeFormRules,
          controlToValidate.value
        );
        if (errors && errors.length) {
          const errorObject: Record<string, string> = {};
          for (const err of errors) {
            errorObject[err.rule] = err.message;
          }
          return errorObject;
        }
      }
      return null;
    };
  }
  vehicleMakeValidator(): ValidatorFn {
    return (controlToValidate: AbstractControl): ValidationErrors | null => {
      const controlName = Object.entries(
        controlToValidate.parent?.controls ?? {}
      ).find(([_, control]) => control === controlToValidate)?.[0];
      if (controlName) {
        const { errors } = sharedFormValidator(
          'vehicleTypeFormRules',
          controlName as keyof typeof vehicleTypeFormRules,
          controlToValidate.value
        );
        if (errors && errors.length) {
          const errorObject: Record<string, string> = {};
          for (const err of errors) {
            errorObject[err.rule] = err.message;
          }
          return errorObject;
        }
      }
      return null;
    };
  }
}
